import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, Bookmark, Share2, MessageCircle, Ruler, Clock, Wallet, Hammer,
  Boxes, Lightbulb, Wrench, Package, ChevronLeft, Trash2, Pencil, Send, CornerDownRight,
} from "lucide-react";
import api, { mediaUrl, brl, DIFFICULTY, timeAgo, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/Loader";
import { CostCalculator } from "@/components/CostCalculator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refreshNotifications } = useAuth();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [liking, setLiking] = useState(false);

  const load = () => {
    api.get(`/projects/slug/${slug}`)
      .then((r) => { setP(r.data); })
      .catch(() => setNotFound(true));
  };

  useEffect(() => { setP(null); setNotFound(false); setActiveImg(0); load(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    if (p) {
      document.title = `${p.title} · PaleteLab`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
      meta.content = (p.description || "").slice(0, 155);
    }
    return () => { document.title = "PaleteLab · Transforme madeira em ideias"; };
  }, [p]);

  const requireAuth = () => {
    if (!user) { navigate("/entrar"); return false; }
    return true;
  };

  const toggleLike = async () => {
    if (!requireAuth() || liking) return;
    setLiking(true);
    try {
      const { data } = await api.post(`/projects/${p.id}/like`);
      setP((prev) => ({ ...prev, is_liked: data.is_liked, likes_count: data.likes_count }));
      refreshNotifications();
    } catch { toast.error("Erro ao curtir"); }
    finally { setLiking(false); }
  };

  const toggleFav = async () => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/projects/${p.id}/favorite`);
      setP((prev) => ({ ...prev, is_favorited: data.is_favorited, favorites_count: data.favorites_count }));
      toast.success(data.is_favorited ? "Projeto salvo nos seus favoritos!" : "Removido dos favoritos");
    } catch { toast.error("Erro ao salvar"); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: p.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado para a área de transferência!"); }
    } catch {}
  };

  const removeProject = async () => {
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Projeto excluído");
      navigate("/painel");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (notFound) return <EmptyState title="Projeto não encontrado." subtitle="Ele pode ter sido removido." action={<Link to="/explorar"><Button className="rounded-full">Explorar projetos</Button></Link>} />;
  if (!p) return <Loader full />;

  const diff = DIFFICULTY[p.difficulty] || DIFFICULTY.facil;
  const gallery = p.images?.length ? p.images.map((i) => i.url) : [p.cover];
  const isOwner = user && (user.id === p.author?.id || user.role === "admin");
  const hasDims = p.width != null || p.height != null || p.depth != null;
  const dimStr = hasDims ? `${p.width ?? "—"}×${p.height ?? "—"}×${p.depth ?? "—"} ${p.unit}` : "—";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Voltar
      </button>

      <div className="grid gap-8 lg:grid-cols-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-secondary">
            <img src={mediaUrl(gallery[activeImg])} alt={p.title} className="aspect-[4/3] w-full object-cover" />
            <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${diff.color}`}>{diff.label}</span>
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                  className={`h-16 w-20 flex-shrink-0 overflow-hidden rounded-lg border-2 ${i === activeImg ? "border-primary" : "border-transparent opacity-70"}`}>
                  <img src={mediaUrl(g)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Header info */}
        <div className="lg:col-span-5">
          <p className="eyebrow text-primary">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}</p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight">{p.title}</h1>

          <Link to={`/u/${p.author?.username}`} className="mt-4 flex items-center gap-3 group">
            <img src={mediaUrl(p.author?.avatar)} alt="" className="h-11 w-11 rounded-full object-cover" />
            <div>
              <p className="font-semibold group-hover:text-primary">{p.author?.name}</p>
              <p className="text-xs text-muted-foreground">@{p.author?.username} · {timeAgo(p.created_at)}</p>
            </div>
          </Link>

          {/* Social actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="like-btn" onClick={toggleLike} variant={p.is_liked ? "default" : "outline"} className="rounded-full gap-1.5">
              <Heart className={`h-4 w-4 ${p.is_liked ? "fill-current like-pop" : ""}`} /> {p.likes_count}
            </Button>
            <Button data-testid="favorite-btn" onClick={toggleFav} variant={p.is_favorited ? "default" : "outline"} className="rounded-full gap-1.5">
              <Bookmark className={`h-4 w-4 ${p.is_favorited ? "fill-current" : ""}`} /> {p.favorites_count}
            </Button>
            <Button data-testid="share-btn" onClick={share} variant="outline" className="rounded-full gap-1.5">
              <Share2 className="h-4 w-4" /> Compartilhar
            </Button>
          </div>

          {isOwner && (
            <div className="mt-3 flex gap-2">
              <Button data-testid="edit-project-btn" onClick={() => navigate(`/publicar?edit=${p.id}`)} variant="secondary" size="sm" className="rounded-full gap-1.5">
                <Pencil className="h-3.5 w-3.5" /> Editar
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="delete-project-btn" variant="ghost" size="sm" className="rounded-full gap-1.5 text-destructive">
                    <Trash2 className="h-3.5 w-3.5" /> Excluir
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
                    <AlertDialogDescription>Esta ação não pode ser desfeita. O projeto e seus comentários serão removidos.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-delete-btn" onClick={removeProject} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {/* Info grid */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <InfoCard icon={Ruler} label="Dimensões" value={dimStr} mono />
            <InfoCard icon={Clock} label="Tempo" value={p.build_time || "—"} />
            <InfoCard icon={Wallet} label="Custo" value={brl(p.cost)} />
            <InfoCard icon={Hammer} label="Dificuldade" value={diff.label} />
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-secondary/50 px-4 py-3">
            <Boxes className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">{p.pallets_count} palete(s) utilizados</span>
          </div>
        </div>
      </div>

      {/* Body sections */}
      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-10">
          <Block title="Sobre o projeto">
            <p className="whitespace-pre-line leading-relaxed text-muted-foreground">{p.description}</p>
          </Block>

          <Block title="Medidas">
            <div className="rounded-2xl border border-border bg-card p-5">
              {hasDims ? (
                <div className="flex flex-wrap gap-6">
                  {p.width != null && <Measure label="Largura" value={`${p.width} ${p.unit}`} />}
                  {p.height != null && <Measure label="Altura" value={`${p.height} ${p.unit}`} />}
                  {p.depth != null && <Measure label="Profundidade" value={`${p.depth} ${p.unit}`} />}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Medidas não informadas pelo autor.</p>
              )}
              <p className="mt-4 text-xs text-muted-foreground italic">Desenhos técnicos poderão ser anexados em breve.</p>
            </div>
          </Block>

          <Block title="Passo a passo" count={p.steps?.length}>
            <div className="space-y-6">
              {p.steps?.length ? p.steps.map((s) => (
                <div key={s.number} data-testid={`step-${s.number}`} className="relative rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-start gap-4">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">{s.number}</span>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{s.title}</h3>
                      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                      {s.images?.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {s.images.map((img, idx) => (
                            <img key={idx} src={mediaUrl(img)} alt={`Etapa ${s.number}`} loading="lazy" className="aspect-video w-full rounded-lg object-cover" />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">Nenhuma etapa cadastrada.</p>}
            </div>
          </Block>

          {p.tips && (
            <Block title="Dicas do autor">
              <div className="flex gap-3 rounded-2xl border border-accent/30 bg-accent/10 p-5">
                <Lightbulb className="h-5 w-5 flex-shrink-0 text-accent" />
                <p className="whitespace-pre-line text-sm leading-relaxed">{p.tips}</p>
              </div>
            </Block>
          )}

          {gallery.length > 1 && (
            <Block title="Fotos do resultado">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {gallery.map((g, i) => (
                  <img key={i} src={mediaUrl(g)} alt="" loading="lazy" className="aspect-square w-full rounded-xl object-cover hover-lift cursor-pointer" onClick={() => setActiveImg(i)} />
                ))}
              </div>
            </Block>
          )}

          <Comments projectId={p.id} user={user} onCount={(n) => setP((prev) => ({ ...prev, comments_count: n }))} refreshNotifications={refreshNotifications} />
        </div>

        {/* Sidebar: materials & tools */}
        <div className="lg:col-span-4 space-y-6">
          <div className="lg:sticky lg:top-20 space-y-6">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="flex items-center gap-2 font-bold"><Package className="h-5 w-5 text-primary" /> Materiais</h3>
              <ul className="mt-4 space-y-3">
                {p.materials?.map((m, i) => (
                  <li key={i} className="flex items-start justify-between gap-2 border-b border-border pb-3 last:border-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      {m.note && <p className="text-xs text-muted-foreground">{m.note}</p>}
                    </div>
                    {m.quantity && <span className="whitespace-nowrap text-xs font-mono text-primary">{m.quantity}</span>}
                  </li>
                ))}
              </ul>
            </div>

            <CostCalculator materials={p.materials || []} title={p.title} />

            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="flex items-center gap-2 font-bold"><Wrench className="h-5 w-5 text-primary" /> Ferramentas</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.tools?.map((t, i) => (
                  <span key={i} className="rounded-full bg-secondary px-3 py-1.5 text-xs font-medium">{t}</span>
                ))}
              </div>
            </div>

            {p.notes && (
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-bold">Observações</h3>
                <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{p.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, mono }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-xs">{label}</span></div>
      <p className={`mt-1 font-bold ${mono ? "font-mono text-sm" : "text-sm"}`}>{value}</p>
    </div>
  );
}

function Measure({ label, value }) {
  return (
    <div className="blueprint-badge rounded-lg px-4 py-2">
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

function Block({ title, count, children }) {
  return (
    <section>
      <h2 className="mb-4 text-xl sm:text-2xl font-bold tracking-tight">
        {title} {count != null && <span className="text-muted-foreground font-normal text-base">({count})</span>}
      </h2>
      {children}
    </section>
  );
}

function Comments({ projectId, user, onCount, refreshNotifications }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const navigate = useNavigate();

  const load = () => api.get(`/projects/${projectId}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const totalCount = (list) => list.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

  const post = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/entrar");
    if (!text.trim()) return;
    try {
      await api.post(`/projects/${projectId}/comments`, { text: text.trim() });
      setText("");
      toast.success("Comentário publicado!");
      const r = await api.get(`/projects/${projectId}/comments`);
      setComments(r.data); onCount?.(totalCount(r.data)); refreshNotifications?.();
    } catch { toast.error("Erro ao comentar"); }
  };

  const postReply = async (parentId) => {
    if (!user) return navigate("/entrar");
    if (!replyText.trim()) return;
    try {
      await api.post(`/projects/${projectId}/comments`, { text: replyText.trim(), parent_id: parentId });
      setReplyText(""); setReplyTo(null);
      const r = await api.get(`/projects/${projectId}/comments`);
      setComments(r.data); onCount?.(totalCount(r.data)); refreshNotifications?.();
    } catch { toast.error("Erro ao responder"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      const r = await api.get(`/projects/${projectId}/comments`);
      setComments(r.data); onCount?.(totalCount(r.data));
    } catch { toast.error("Erro ao excluir"); }
  };

  return (
    <section data-testid="comments-section">
      <h2 className="mb-4 flex items-center gap-2 text-xl sm:text-2xl font-bold tracking-tight">
        <MessageCircle className="h-5 w-5 text-primary" /> Comentários {comments && `(${totalCount(comments)})`}
      </h2>

      <form onSubmit={post} className="mb-6 flex gap-3">
        <img src={mediaUrl(user?.avatar) || "https://ui-avatars.com/api/?name=?&background=ddd"} alt="" className="h-9 w-9 rounded-full object-cover" />
        <div className="flex-1">
          <Textarea data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={user ? "Deixe um comentário..." : "Entre para comentar"} className="min-h-[80px] rounded-xl resize-none" />
          <div className="mt-2 flex justify-end">
            <Button data-testid="comment-submit-btn" type="submit" className="rounded-full gap-1.5"><Send className="h-4 w-4" /> Comentar</Button>
          </div>
        </div>
      </form>

      {comments === null ? <Loader /> : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">Seja o primeiro a comentar!</p>
      ) : (
        <div className="space-y-5">
          {comments.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <img src={mediaUrl(c.author?.avatar)} alt="" className="h-9 w-9 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{c.author?.name}</span>
                    <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed">{c.text}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
                      <CornerDownRight className="h-3 w-3" /> Responder
                    </button>
                    {user && (user.id === c.author?.id || user.role === "admin") && (
                      <button onClick={() => del(c.id)} className="text-xs text-destructive hover:underline">Excluir</button>
                    )}
                  </div>

                  {replyTo === c.id && (
                    <div className="mt-3 flex gap-2">
                      <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Sua resposta..." className="min-h-[60px] rounded-xl resize-none" data-testid={`reply-input-${c.id}`} />
                      <Button size="sm" onClick={() => postReply(c.id)} className="self-end rounded-full" data-testid={`reply-submit-${c.id}`}>Enviar</Button>
                    </div>
                  )}

                  {c.replies?.length > 0 && (
                    <div className="mt-3 space-y-3 border-l-2 border-border pl-4">
                      {c.replies.map((r) => (
                        <div key={r.id} className="flex items-start gap-2">
                          <img src={mediaUrl(r.author?.avatar)} alt="" className="h-7 w-7 rounded-full object-cover" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{r.author?.name}</span>
                              <span className="text-xs text-muted-foreground">{timeAgo(r.created_at)}</span>
                            </div>
                            <p className="mt-0.5 text-sm">{r.text}</p>
                          </div>
                          {user && (user.id === r.author?.id || user.role === "admin") && (
                            <button onClick={() => del(r.id)} className="text-xs text-destructive hover:underline">Excluir</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
